import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SeoArticleSchema from "@/components/SeoArticleSchema";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";
import { InsightKeyTakeaway } from "@/components/InsightKeyTakeaway";
import PortugalSpainQuickFitCheck from "@/components/PortugalSpainQuickFitCheck";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const sections = [
  {
    title: "What usually separates the strongest options for Americans?",
    paragraphs: [
      "For Americans, the best country usually is not the one with the most hype. It is the one where long-term stay practicality, healthcare confidence, affordability, and daily life hold together at the same time.",
      "That is why the shortlist often changes once you stop asking where other people moved and start asking what would actually work for you.",
    ],
  },
  {
    title: "Which practical filters are worth using first?",
    bullets: [
      "how realistic the residency path looks from a U.S. starting point",
      "whether healthcare feels strong enough for your stage of life",
      "what monthly life there would actually cost in the city or region you would choose",
      "how much infrastructure, language comfort, and daily convenience matter to you",
    ],
  },
  {
    title: "Why does the answer change by profile?",
    paragraphs: [
      "Portugal, Spain, Mexico, Panama, Costa Rica, and parts of Europe may all stay in the conversation, but for different reasons. Some feel easier to settle into. Others score better on healthcare or infrastructure. Others work because the numbers hold up longer.",
      "A better question than ‘best for Americans’ is which countries make the most sense once your budget, timeline, and move goals are doing the sorting.",
    ],
  },
  {
    title: "What should a useful comparison actually surface?",
    bullets: [
      "where the move looks realistic now, not just attractive in theory",
      "which tradeoffs would matter most once you are living there",
      "what would still need deeper checking before a country becomes the lead",
    ],
  },
];

const AMERICANS_QUICK_FIT_QUESTIONS = [
  {
    id: "healthcare_priority",
    question: "What should carry more weight in the move?",
    options: [
      { label: "Healthcare confidence", scores: { ES: 2, PT: 1, FR: 1 } },
      { label: "Budget flexibility", scores: { MX: 2, PT: 1, PA: 1 } },
    ],
  },
  {
    id: "residency_simplicity",
    question: "How important is keeping the residency path straightforward?",
    options: [
      { label: "Very important", scores: { PT: 2, MX: 1, PA: 1 } },
      { label: "I can handle more complexity for stronger upside", scores: { ES: 1, FR: 1, CR: 1 } },
    ],
  },
  {
    id: "climate_and_pace",
    question: "What kind of daily life sounds more appealing?",
    options: [
      { label: "Warm climate and a softer pace", scores: { PT: 1, MX: 2, CR: 1, PA: 1 } },
      { label: "More structure, walkability, and established systems", scores: { ES: 2, FR: 1 } },
    ],
  },
  {
    id: "english_accessibility",
    question: "How important is easier English accessibility?",
    options: [
      { label: "I want a softer language transition", scores: { PT: 2, MX: 1, PA: 1 } },
      { label: "I can adapt if the overall fit is stronger", scores: { ES: 1, FR: 1, CR: 1 } },
    ],
  },
  {
    id: "overall_direction",
    question: "What feels more like the move you want?",
    options: [
      { label: "A cost-aware move that still feels practical", scores: { MX: 2, PT: 1, PA: 1 } },
      { label: "A systems-first move with stronger infrastructure", scores: { ES: 2, FR: 1, PT: 1 } },
    ],
  },
];

const AMERICANS_COUNTRY_LABELS = {
  PT: "Portugal",
  ES: "Spain",
  MX: "Mexico",
  PA: "Panama",
  CR: "Costa Rica",
  FR: "France",
};

const AMERICANS_COUNTRY_EXPLANATIONS = {
  PT: "Portugal often rises when residency practicality, day-to-day ease, healthcare confidence, and a softer transition all matter together.",
  ES: "Spain often rises when healthcare confidence, infrastructure, walkability, and broader regional variety carry more weight than keeping costs as low as possible.",
  MX: "Mexico often rises when affordability, climate, and keeping everyday costs workable matter most from an American starting point.",
  PA: "Panama often rises when budget, accessibility, and a relatively familiar transition matter more than deeper European-system advantages.",
  CR: "Costa Rica often rises when climate, lifestyle pace, and a steady quality-of-life feel matter more than maximizing budget efficiency.",
  FR: "France often rises when system strength, healthcare confidence, and long-term structure matter enough to justify more complexity or higher cost pressure.",
};

const AMERICANS_FAQ_ITEMS = [
  {
    question: "Can one country really be ‘best’ for all Americans moving abroad?",
    answer:
      "No. Americans start from different budgets, healthcare expectations, tax realities, work situations, and lifestyle goals. The stronger answer depends on which tradeoffs matter most in your version of the move.",
  },
  {
    question: "Why do Portugal, Spain, Mexico, and Panama keep showing up so often?",
    answer:
      "They stay in the conversation for different reasons. Some are easier on budget. Some feel stronger on healthcare or infrastructure. Some feel easier to pursue from a U.S. starting point. The right lead changes once those reasons are weighted together.",
  },
  {
    question: "Should residency simplicity matter as much as lifestyle fit?",
    answer:
      "Usually yes. A country can look exciting in theory but lose ground quickly if the practical path feels too fragile or complicated to sustain. Long-term fit usually depends on both the life and the logistics.",
  },
  {
    question: "If a close alternative also looks strong, what should I do next?",
    answer:
      "Treat that as a sign that your decision needs a fuller comparison, not that the Quick Fit™ failed. The full assessment looks across more factors at once and explains why one country may still edge out another when the full picture is visible.",
  },
];

const buildAmericansQuickFitResult = ({ lead, closeAlternativeNames = [] }) => ({
  headline: `${lead.name} may deserve the first closer look from a U.S. starting point`,
  body: AMERICANS_COUNTRY_EXPLANATIONS[lead.code] || `${lead.name} appears to align most closely with the priorities you selected.`,
  supportingNote: closeAlternativeNames.length
    ? closeAlternativeNames.length === 1
      ? `${closeAlternativeNames[0]} also came through as a strong match from the priorities you selected. The full Relocation Clarity Assessment considers additional factors that may still change the final recommendation.`
      : `${closeAlternativeNames.slice(0, -1).join(", ")} and ${closeAlternativeNames[closeAlternativeNames.length - 1]} also came through as strong matches from the priorities you selected. The full Relocation Clarity Assessment considers additional factors that may still change the final recommendation.`
    : "This is a directional result, not a final recommendation. The full Relocation Clarity Assessment considers additional factors that may still change the final recommendation.",
});

export default function BestCountriesForAmericansToMoveAbroad() {
  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="best-countries-americans-page">
      <SeoArticleSchema
        headline="Best Countries for Americans to Move Abroad"
        description="Compare the countries Americans most often consider for living abroad across residency practicality, healthcare, affordability, and day-to-day fit."
        pathname="/insights/best-countries-for-americans-to-move-abroad"
        keywords={[
          "best countries for americans to move abroad",
          "where should americans move abroad",
          "best countries for americans overseas",
        ]}
      />
      <Header />
      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-10 shadow-[0_18px_48px_rgba(28,25,23,0.06)] sm:px-10" data-testid="best-countries-americans-hero-section">
            <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" />
            <p className="rc-section-kicker mb-4">Relocation insight</p>
            <h1 className="font-manrope text-4xl text-stone-900 sm:text-5xl" data-testid="best-countries-americans-page-title">Best Countries for Americans to Move Abroad</h1>
            <div className="mt-6 max-w-4xl space-y-4 text-base leading-8 text-stone-600">
              <p>The strongest options for Americans usually separate on residency practicality, healthcare confidence, affordability, and how easy daily life feels once the move becomes real.</p>
              <p>That is why the right shortlist looks different for retirees, remote workers, families, and people still early in the decision.</p>
            </div>
          </section>

          <InsightKeyTakeaway
            testIdPrefix="best-countries-americans-key-takeaway"
            paragraphs={[
              "There is no single best country for Americans to move abroad. The strongest option depends on how residency practicality, healthcare confidence, affordability, taxes, and day-to-day fit come together for your situation.",
              "Countries that often appeal to Americans do so for different reasons. That is why the more useful question is not which destination is most popular, but which one still looks workable once your real priorities are doing the filtering.",
            ]}
          />

          <PortugalSpainQuickFitCheck
            rootTestId="best-countries-americans-quick-fit"
            quickFitType="americans_broad"
            questions={AMERICANS_QUICK_FIT_QUESTIONS}
            countryLabels={AMERICANS_COUNTRY_LABELS}
            countryPriorityOrder={["PT", "ES", "MX", "PA", "CR", "FR"]}
            buildResult={buildAmericansQuickFitResult}
          />

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="best-countries-americans-faq-section">
            <div className="max-w-4xl">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500" data-testid="best-countries-americans-faq-kicker">Questions people often still have</p>
              <h2 className="mt-3 font-manrope text-3xl text-stone-900" data-testid="best-countries-americans-faq-title">Still Narrowing the Best Options for Americans?</h2>
              <p className="mt-4 text-base leading-8 text-stone-600" data-testid="best-countries-americans-faq-description">
                These are the questions that usually come up after a few countries begin to look strong but before the decision feels settled enough to act on.
              </p>
            </div>

            <Accordion type="single" collapsible className="mt-7 space-y-3" data-testid="best-countries-americans-faq-accordion">
              {AMERICANS_FAQ_ITEMS.map((item, index) => (
                <AccordionItem key={item.question} value={`americans-faq-${index + 1}`} className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5" data-testid={`best-countries-americans-faq-item-${index + 1}`}>
                  <AccordionTrigger className="py-5 text-left font-manrope text-lg text-stone-900 no-underline hover:no-underline" data-testid={`best-countries-americans-faq-trigger-${index + 1}`}>
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="pb-5 text-sm leading-7 text-stone-600" data-testid={`best-countries-americans-faq-answer-${index + 1}`}>
                      {item.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="best-countries-americans-sections-grid">
            {sections.map((section, index) => (
              <article key={section.title} className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid={`best-countries-americans-section-${index + 1}`}>
                <h2 className="font-manrope text-2xl text-stone-900">{section.title}</h2>
                <div className="mt-5 space-y-4 text-base leading-8 text-stone-600">
                  {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
                {section.bullets?.length ? (
                  <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-600">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400/80" /><span>{bullet}</span></li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="best-countries-americans-internal-linking-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="best-countries-americans-internal-linking-title">What should you read next if you are still early in the decision?</h2>
            <div className="mt-5 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="best-countries-americans-internal-linking-copy">
              <p>
                If you are still deciding what should matter most, start with{" "}
                <Link to="/insights/what-country-should-i-move-to" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="best-countries-americans-what-country-link">
                  what actually determines country fit
                </Link>
                . If you already have a wide list, move next to{" "}
                <Link to="/insights/create-country-shortlist-for-moving-abroad" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="best-countries-americans-shortlist-link">
                  building a realistic shortlist
                </Link>
                .
              </p>
              <p>
                If retirement is your main lens, the comparison often gets sharper in{" "}
                <Link to="/insights/portugal-vs-spain-for-american-retirees" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="best-countries-americans-retirees-link">
                  Portugal vs Spain for American retirees
                </Link>
                , because it shows how two strong contenders separate once healthcare, affordability, and daily rhythm are weighed together.
              </p>
            </div>
          </section>

          <ContextualLinkPanel
            kicker="Continue exploring"
            title="If you are not ready for the full assessment yet, these are the most useful next pages from here."
            description="If this page helped surface likely contenders, the next useful move is usually to narrow the shortlist, look at a real comparison, and preview how the Blueprint handles the final decision."
            testIdPrefix="best-countries-americans-next-steps"
            items={[
              {
                slug: "shortlist",
                eyebrow: "Narrow the field",
                title: "Create a realistic shortlist",
                description: "Cut broad interest down to a smaller set of countries that still hold up for your life and goals.",
                ctaLabel: "Build the shortlist",
                to: "/insights/create-country-shortlist-for-moving-abroad",
              },
              {
                slug: "comparison",
                eyebrow: "See a live example",
                title: "Compare Portugal and Spain",
                description: "See how two common U.S. starting-point contenders separate once tradeoffs become more practical.",
                ctaLabel: "Open the comparison",
                to: "/insights/portugal-vs-spain-living-abroad",
              },
              {
                slug: "sample-blueprint",
                eyebrow: "See the paid product",
                title: "Preview a Sample Blueprint",
                description: "See how a shortlist becomes a fuller recommendation with context, tradeoffs, and next steps.",
                ctaLabel: "Preview the sample",
                to: "/sample-plan",
              },
            ]}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}